import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Leaf, Send, Loader2, ArrowLeft, MessageCircle, Mail, User, Bot
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProGardenerChat = () => {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showEscalateOption, setShowEscalateOption] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationHistory]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage("");
    setShowEscalateOption(false);
    
    // Add user message to history
    setConversationHistory(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/pro-chat`,
        { 
          message: userMessage,
          conversation_history: conversationHistory 
        },
        { withCredentials: true }
      );

      // Add assistant response to history
      setConversationHistory(prev => [...prev, { 
        role: "assistant", 
        content: response.data.response 
      }]);

      // Check if escalation is suggested
      if (response.data.needs_escalation) {
        setShowEscalateOption(true);
      }
    } catch (error) {
      console.error("Chat error:", error);
      if (error.response?.status === 403) {
        toast.error("Pro Gardener Chat requires a Master Gardener subscription");
      } else {
        toast.error("Failed to send message. Please try again.");
      }
      // Remove the user message if failed
      setConversationHistory(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleEscalate = async () => {
    setEscalating(true);
    
    try {
      // Create summary from last few messages
      const recentMessages = conversationHistory.slice(-4);
      const issueSummary = recentMessages
        .filter(m => m.role === "user")
        .map(m => m.content)
        .join(" | ");

      const response = await axios.post(
        `${API}/pro-chat/escalate`,
        { 
          issue_summary: issueSummary,
          conversation_history: conversationHistory 
        },
        { withCredentials: true }
      );

      toast.success("Your request has been sent to our team!");
      setConversationHistory(prev => [...prev, { 
        role: "system", 
        content: `Support ticket created (ID: ${response.data.ticket_id}). We'll respond to your email within 24 hours.` 
      }]);
      setShowEscalateOption(false);
    } catch (error) {
      console.error("Escalation error:", error);
      toast.error("Failed to create support ticket. Please try again.");
    } finally {
      setEscalating(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/saved"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-secondary" />
                Pro Gardener Chat
              </h1>
              <p className="text-sm text-muted-foreground">Your personal gardening expert</p>
            </div>
          </div>
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="RootSpace.Pro" className="h-8 w-auto" />
            
          </Link>
        </div>
      </header>

      <main className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-6 py-4">
        <Card className="card-organic flex-1 flex flex-col min-h-0">
          <CardHeader className="pb-2">
            <CardTitle className="font-serif text-lg flex items-center gap-2">
              Chat with Your Pro Gardener
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Ask anything about gardening, permaculture, or your ROOTSPACE plans
            </p>
          </CardHeader>
          
          <CardContent className="flex-1 flex flex-col min-h-0 pt-2">
            {/* Messages Area */}
            <div 
              className="flex-1 overflow-y-auto space-y-4 pr-2 min-h-[400px] max-h-[500px]"
              data-testid="chat-messages"
            >
              {conversationHistory.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Welcome, {user?.name}!</p>
                  <p className="text-sm mt-2">
                    I&apos;m your Pro Gardener. Ask me about companion planting, pest control, seasonal tasks, or anything else!
                  </p>
                </div>
              )}

              {conversationHistory.map((msg, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex gap-3 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role !== "user" && (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === "system" ? "bg-amber-100" : "bg-primary/10"
                    }`}>
                      {msg.role === "system" ? (
                        <Mail className="w-4 h-4 text-amber-600" />
                      ) : (
                        <Bot className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  )}
                  
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === "user" 
                      ? "bg-primary text-primary-foreground rounded-br-md" 
                      : msg.role === "system"
                        ? "bg-amber-50 text-amber-800 border border-amber-200"
                        : "bg-muted rounded-bl-md"
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>

                  {msg.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-secondary" />
                    </div>
                  )}
                </motion.div>
              ))}

              {loading && (
                <div className="flex gap-3 justify-start">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-primary" />
                  </div>
                  <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Thinking...
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Escalation Option */}
            {showEscalateOption && (
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"
              >
                <p className="text-sm text-amber-800 mb-3">
                  Would you like me to escalate this to our support team? They&apos;ll respond to your email within 24 hours.
                </p>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowEscalateOption(false)}
                  >
                    Continue Chatting
                  </Button>
                  <Button 
                    size="sm"
                    onClick={handleEscalate}
                    disabled={escalating}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="escalate-btn"
                  >
                    {escalating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Email Support Team
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Input Area */}
            <form onSubmit={handleSendMessage} className="mt-4 flex gap-2">
              <Input
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask about gardening, permaculture, app features..."
                className="flex-1"
                disabled={loading}
                data-testid="chat-input"
              />
              <Button 
                type="submit" 
                disabled={!message.trim() || loading}
                className="btn-primary"
                data-testid="send-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProGardenerChat;
